<!-- src/lib/components/WorkloadView.svelte -->
<script lang="ts">
  import { pmStore } from '../stores/pm.svelte.ts';

  // Build column distribution matrix
  // For each unique column name, count cards by priority
  const PRIORITY_ORDER = ['urgent', 'high', 'medium', 'low', 'none'] as const;
  const PRIORITY_LABELS: Record<string, string> = {
    urgent: 'Urgent', high: 'High', medium: 'Medium', low: 'Low', none: 'None'
  };
  const PRIORITY_COLORS: Record<string, string> = {
    urgent: 'oklch(0.62 0.26 25)', high: 'oklch(0.78 0.16 85)', medium: 'oklch(0.58 0.22 265)', low: 'rgba(255,255,255,0.3)', none: 'rgba(255,255,255,0.15)'
  };

  interface ColumnRow {
    name: string;
    counts: Record<string, number>;  // priority -> count
    total: number;
  }

  // Aggregate all columns from all loaded board details
  const columnDistribution: ColumnRow[] = $derived((() => {
    const map = new Map<string, Record<string, number>>();

    for (const bd of Object.values(pmStore.boardDetails)) {
      for (const col of bd.columns) {
        const existing = map.get(col.name) ?? Object.fromEntries(PRIORITY_ORDER.map(p => [p, 0]));
        for (const card of col.cards) {
          existing[card.priority] = (existing[card.priority] ?? 0) + 1;
        }
        map.set(col.name, existing);
      }
    }

    return [...map.entries()].map(([name, counts]) => ({
      name,
      counts,
      total: Object.values(counts).reduce((a, b) => a + b, 0),
    })).sort((a, b) => b.total - a.total);
  })());

  const priorityTotals: Record<string, number> = $derived((() => {
    const totals: Record<string, number> = {};
    for (const p of PRIORITY_ORDER) {
      totals[p] = columnDistribution.reduce((sum, row) => sum + (row.counts[p] ?? 0), 0);
    }
    return totals;
  })());

  const grandTotal: number = $derived(
    Object.values(priorityTotals).reduce((a, b) => a + b, 0)
  );

  // For heatmap: max cell value
  const maxCellValue: number = $derived(
    Math.max(1, ...columnDistribution.flatMap(row =>
      PRIORITY_ORDER.map(p => row.counts[p] ?? 0)
    ))
  );

  function heatmapBg(count: number): string {
    if (count === 0) return 'transparent';
    const intensity = count / maxCellValue;
    return `rgba(0, 255, 136, ${intensity * 0.18})`;
  }

  // Project breakdown
  interface ProjectBreakdown {
    name: string;
    columns: { name: string; count: number; color: string }[];
    maxCount: number;
  }

  const projectBreakdowns: ProjectBreakdown[] = $derived((() => {
    return Object.values(pmStore.boardDetails).map(bd => {
      const project = pmStore.projects.find(p => p.id === bd.project_id);
      const columns = bd.columns.map(col => ({
        name: col.name,
        count: col.cards.length,
        color: col.color,
      }));
      const maxCount = Math.max(1, ...columns.map(c => c.count));
      return {
        name: project?.name ?? 'Unknown Project',
        columns,
        maxCount,
      };
    }).filter(pb => pb.columns.length > 0);
  })());
</script>

<div class="workload-view">
  <header class="view-header">
    <h2>Workload</h2>
    <span class="total-badge">{grandTotal} cards total</span>
  </header>

  {#if grandTotal === 0}
    <div class="empty-state">
      <p>No cards loaded yet. Open a board to see workload distribution.</p>
    </div>
  {:else}
    <!-- Section 1: Distribution Matrix -->
    <section class="workload-section">
      <h3 class="section-title">Distribution by Status × Priority</h3>
      <div class="matrix-wrap">
        <table class="matrix-table">
          <thead>
            <tr>
              <th class="col-status">Status</th>
              {#each PRIORITY_ORDER as p}
                <th class="col-priority" style="color: {PRIORITY_COLORS[p]}">
                  {PRIORITY_LABELS[p]}
                </th>
              {/each}
              <th class="col-total">Total</th>
            </tr>
          </thead>
          <tbody>
            {#each columnDistribution as row}
              <tr>
                <td class="row-name">{row.name}</td>
                {#each PRIORITY_ORDER as p}
                  <td class="count-cell" style="background: {heatmapBg(row.counts[p] ?? 0)}">
                    {row.counts[p] ?? 0}
                  </td>
                {/each}
                <td class="total-cell">{row.total}</td>
              </tr>
            {/each}
          </tbody>
          <tfoot>
            <tr>
              <td class="row-name foot-label">Total</td>
              {#each PRIORITY_ORDER as p}
                <td class="count-cell foot-cell">{priorityTotals[p] ?? 0}</td>
              {/each}
              <td class="total-cell foot-total">{grandTotal}</td>
            </tr>
          </tfoot>
        </table>
      </div>
    </section>

    <!-- Section 2: Per-project breakdown -->
    {#if projectBreakdowns.length > 0}
      <section class="workload-section">
        <h3 class="section-title">Per-Project Column Breakdown</h3>
        {#each projectBreakdowns as pb}
          <div class="project-block">
            <div class="project-name">{pb.name}</div>
            {#each pb.columns as col}
              <div class="col-bar-row">
                <span class="col-bar-label">{col.name}</span>
                <div class="col-bar-track">
                  <div
                    class="col-bar-fill"
                    style="width: {(col.count / pb.maxCount) * 100}%; background: {col.color !== '#444' ? col.color : 'oklch(0.74 0.28 310 / 0.46)'};"
                  ></div>
                </div>
                <span class="col-bar-count">{col.count}</span>
              </div>
            {/each}
          </div>
        {/each}
      </section>
    {/if}
  {/if}
</div>

<style>
  .workload-view {
    padding: 1.5rem;
    max-width: 900px;
    height: 100%;
    overflow-y: auto;
  }

  .view-header {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    margin-bottom: 1.5rem;
  }

  h2 { font-size: 1.1rem; color: var(--text-primary, oklch(92% 0.01 270)); margin: 0; }

  .total-badge {
    font-size: 0.72rem;
    color: rgba(255,255,255,0.35);
    background: rgba(255,255,255,0.05);
    padding: 0.2rem 0.5rem;
    border-radius: 999px;
    border: 1px solid rgba(255,255,255,0.08);
  }

  .workload-section { margin-bottom: 2rem; }

  .section-title {
    font-size: 0.7rem;
    text-transform: uppercase;
    letter-spacing: 0.1em;
    color: rgba(255,255,255,0.3);
    margin: 0 0 0.875rem;
    font-weight: 600;
  }

  .matrix-wrap { overflow-x: auto; }

  .matrix-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 0.8rem;
  }

  .matrix-table th, .matrix-table td {
    padding: 0.5rem 0.75rem;
    text-align: right;
    border-bottom: 1px solid rgba(255,255,255,0.05);
  }

  .matrix-table th {
    font-size: 0.68rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: rgba(255,255,255,0.3);
    background: rgba(255,255,255,0.02);
  }

  .col-status { text-align: left; }
  .row-name { text-align: left; color: var(--text-primary, oklch(92% 0.01 270)); font-weight: 500; }
  .count-cell {
    color: rgba(255,255,255,0.6);
    border-radius: 4px;
    transition: background 200ms;
    font-variant-numeric: tabular-nums;
  }
  .count-cell:hover { background: rgba(255,255,255,0.06) !important; }
  .total-cell { color: oklch(92% 0.01 270); font-weight: 600; border-left: 1px solid rgba(255,255,255,0.06); }
  tfoot .foot-label { color: rgba(255,255,255,0.3); font-size: 0.68rem; text-transform: uppercase; letter-spacing: 0.06em; }
  tfoot .foot-cell { color: rgba(255,255,255,0.5); border-top: 1px solid rgba(255,255,255,0.08); }
  tfoot .foot-total { color: oklch(80% 0.2 145); border-top: 1px solid rgba(255,255,255,0.08); }

  .project-block {
    background: rgba(255,255,255,0.02);
    border: 1px solid rgba(255,255,255,0.05);
    border-radius: 8px;
    padding: 0.875rem 1rem;
    margin-bottom: 0.75rem;
  }
  .project-name {
    font-size: 0.8rem;
    font-weight: 600;
    color: var(--text-primary, oklch(92% 0.01 270));
    margin-bottom: 0.625rem;
  }
  .col-bar-row {
    display: flex;
    align-items: center;
    gap: 0.625rem;
    margin-bottom: 0.3rem;
  }
  .col-bar-label {
    font-size: 0.72rem;
    color: rgba(255,255,255,0.4);
    width: 7rem;
    flex-shrink: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .col-bar-track {
    flex: 1;
    height: 5px;
    background: rgba(255,255,255,0.06);
    border-radius: 999px;
    overflow: hidden;
  }
  .col-bar-fill {
    height: 100%;
    border-radius: 999px;
    transition: width 300ms ease;
    min-width: 2px;
  }
  .col-bar-count {
    font-size: 0.68rem;
    color: rgba(255,255,255,0.3);
    width: 1.5rem;
    text-align: right;
    font-variant-numeric: tabular-nums;
  }
  .empty-state {
    text-align: center;
    padding: 4rem 2rem;
    color: rgba(255,255,255,0.25);
    font-size: 0.85rem;
  }
</style>
